import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../services/api';
import { Trophy, Play, Lock, Megaphone, Pencil, Settings2, X } from 'lucide-react';
import { formatCycleDate, computeCycleEnd } from '../../utils/cycleUtils';
import { normalizePhotoSlot } from '../../constants/progressPhotoSlots';
import ProgressPhotoImage from './ProgressPhotoImage';
import './d28d-admin-surfaces.css';

const STATUS_LABEL = {
  draft: 'Borrador',
  active: 'Activo',
  closed: 'Cerrado',
  published: 'Podio publicado',
  cancelled: 'Cancelado',
};

function cycleWindow(cycle) {
  const start = cycle?.startDate || cycle?.start_date;
  if (!start) return { start: '', end: '' };
  const end = computeCycleEnd(start);
  return {
    start: typeof start === 'string' ? start.slice(0, 10) : start,
    end: end instanceof Date ? end.toISOString().slice(0, 10) : String(end).slice(0, 10),
  };
}

function EvidenceThumb({ src, alt, className = 'evidence-thumb mt-1' }) {
  return <ProgressPhotoImage src={src} alt={alt} className={className} />;
}

const EVAL_PHOTO_SLOTS = [
  { slot: 'frente_inicio', label: 'Inicio — Frente' },
  { slot: 'lateral_inicio', label: 'Inicio — Lateral' },
  { slot: 'espalda_inicio', label: 'Inicio — Espalda' },
  { slot: 'detalle_inicio', label: 'Inicio — Detalle' },
  { slot: 'frente_fin', label: 'Fin — Frente' },
  { slot: 'lateral_fin', label: 'Fin — Lateral' },
  { slot: 'espalda_fin', label: 'Fin — Espalda' },
  { slot: 'detalle_fin', label: 'Fin — Detalle' },
];

function photosBySlot(progressPhotos = []) {
  const map = new Map();
  (progressPhotos || []).forEach((photo) => {
    const raw = String(photo?.slot || '').trim();
    if (!raw) return;
    const slot = normalizePhotoSlot(raw) || raw;
    if (!map.has(slot)) map.set(slot, photo);
  });
  return map;
}

function ParticipantPhotoGrid({ photos = [] }) {
  const bySlot = photosBySlot(photos);
  const inicio = EVAL_PHOTO_SLOTS.slice(0, 4);
  const fin = EVAL_PHOTO_SLOTS.slice(4);
  const renderSlot = ({ slot, label }) => {
    const photo = bySlot.get(slot);
    return (
      <div key={slot} className="border border-slate-200 rounded-lg p-2 bg-slate-50 text-sm min-h-[11rem] flex flex-col">
        <p className="text-xs font-semibold uppercase text-stone-500 mb-1">{label}</p>
        {photo?.url ? (
          <EvidenceThumb src={photo.url} alt={label} className="evaluate-photo-thumb mt-1 flex-1" />
        ) : (
          <div className="evaluate-photo-placeholder flex-1 flex items-center justify-center text-xs text-stone-400 border border-dashed border-slate-300 rounded-md">
            Sin foto
          </div>
        )}
      </div>
    );
  };
  return (
    <div className="space-y-4 mt-2">
      <div>
        <p className="text-xs font-semibold text-stone-700 mb-2">Fotos inicio de ciclo</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {inicio.map(renderSlot)}
        </div>
      </div>
      <div>
        <p className="text-xs font-semibold text-stone-700 mb-2">Fotos fin de ciclo</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {fin.map(renderSlot)}
        </div>
      </div>
    </div>
  );
}

const FITNESS_EVAL_COLUMNS = [
  { key: 'abdominales', label: 'Abdominales', weightKey: 'abdominales_kg' },
  { key: 'burpees', label: 'Burpees' },
  { key: 'flexiones', label: 'Flexiones' },
  { key: 'sentadilla', label: 'Sentadilla', weightKey: 'sentadilla_kg' },
  { key: 'plancha', label: 'Plancha', unit: 'time' },
];

function formatFitnessValue(col, scores = {}) {
  const value = scores?.[col.key];
  if (value == null || value === '') return null;
  if (col.unit === 'time') {
    const sec = Math.max(0, Number(value) || 0);
    const min = Math.floor(sec / 60);
    const rem = sec % 60;
    return `${min}:${String(rem).padStart(2, '0')}`;
  }
  const weight = col.weightKey ? scores?.[col.weightKey] : null;
  if (weight != null && Number(weight) > 0) return `${value} (${weight} kg)`;
  return String(value);
}

function parseFitnessFromEvidences(evidences = []) {
  const fitEv = (evidences || []).find((e) => e.tipo === 'fitness_test');
  if (!fitEv?.contenido) return { scores: null, total: null, testName: null };
  try {
    const parsed = typeof fitEv.contenido === 'string' ? JSON.parse(fitEv.contenido) : fitEv.contenido;
    return {
      scores: parsed?.scores || null,
      total: parsed?.total ?? null,
      testName: parsed?.test_name || null,
    };
  } catch {
    return { scores: null, total: null, testName: null };
  }
}

function ParticipantFitnessGrid({
  scores = null,
  total = null,
  testName = null,
  evidences = [],
}) {
  const fromEv = parseFitnessFromEvidences(evidences);
  const resolvedScores = scores || fromEv.scores;
  const resolvedTotal = total ?? fromEv.total;
  const resolvedName = testName || fromEv.testName;
  if (!resolvedScores && resolvedTotal == null) {
    return (
      <div className="mt-3 border border-dashed border-slate-300 rounded-xl p-3 bg-slate-50">
        <p className="text-xs font-semibold text-stone-700 mb-1">Prueba física</p>
        <p className="text-xs text-stone-500">Sin resultado de prueba física en este ciclo.</p>
      </div>
    );
  }
  return (
    <div className="mt-3 space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-xs font-semibold text-stone-700">Prueba física</p>
        <p className="text-xs text-stone-500">
          {resolvedName ? `${resolvedName} · ` : ''}
          {resolvedTotal != null ? `Total: ${resolvedTotal} pts` : 'Sin total'}
        </p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {FITNESS_EVAL_COLUMNS.map((col) => {
          const display = formatFitnessValue(col, resolvedScores || {});
          return (
            <div key={col.key} className="border border-slate-200 rounded-xl p-3 bg-slate-50 text-sm min-h-[5.5rem] flex flex-col">
              <p className="text-xs font-semibold uppercase text-stone-500 mb-2">{col.label}</p>
              <p className={`text-lg font-bold ${display ? 'text-stone-900' : 'text-stone-400'}`}>
                {display || '—'}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function defaultPremioName(cycle) {
  return `Premiación — ${cycle.name || cycle.label || `Ciclo ${cycle.id}`}`;
}

function hydratePodiumFromData(data, setPodium) {
  const pod = { first: '', second: '', third: '' };
  (data?.podium || []).forEach((p) => {
    if (Number(p.lugar) === 1) pod.first = String(p.entry_id);
    if (Number(p.lugar) === 2) pod.second = String(p.entry_id);
    if (Number(p.lugar) === 3) pod.third = String(p.entry_id);
  });
  setPodium(pod);
}

export default function D28dChallengesAdmin({ onBack, scopeGymId = null, gymScoped = false }) {
  const [cycles, setCycles] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [configCycle, setConfigCycle] = useState(null);
  const [configForm, setConfigForm] = useState({
    premio: '1er lugar: $700.000 · 2do: $200.000 · 3ro: $100.000 COP',
    objetivo: '',
    descripcion: '',
    program_id: 'vital',
    min_classes: 8,
    instagram_live_url: '',
  });
  const [selected, setSelected] = useState(null);
  const [scores, setScores] = useState({});
  const [podium, setPodium] = useState({ first: '', second: '', third: '' });
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [configErr, setConfigErr] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const cycleParams = gymScoped && scopeGymId
      ? { gym_id: scopeGymId, gym_only: 'true' }
      : { platform_only: 'true' };
    const programParams = gymScoped && scopeGymId
      ? { gym_id: scopeGymId, gym_only: 'true' }
      : { platform_only: 'true' };
    const challengeParams = gymScoped && scopeGymId
      ? { gym_id: scopeGymId }
      : { platform_only: 'true' };
    setErr('');
    const [cRes, pRes, chRes] = await Promise.allSettled([
      api.get('/cycles', { params: cycleParams }),
      api.get('/programs', { params: programParams }),
      api.get('/d28d/challenges', { params: challengeParams }),
    ]);
    if (cRes.status === 'fulfilled') {
      setCycles(cRes.value.data?.data || cRes.value.data || []);
    } else {
      setCycles([]);
      setErr('No se pudieron cargar los ciclos.');
    }
    if (pRes.status === 'fulfilled') {
      setPrograms(pRes.value.data?.data || pRes.value.data || []);
    } else {
      setPrograms([]);
    }
    if (chRes.status === 'fulfilled') {
      setChallenges(chRes.value.data?.data || []);
    } else {
      setChallenges([]);
      setErr(chRes.reason?.response?.data?.error || 'No se pudieron cargar las premiaciones.');
    }
  }, [gymScoped, scopeGymId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!configCycle) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setConfigCycle(null);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [configCycle]);

  useEffect(() => {
    if (!selected) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setSelected(null);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [selected]);

  const closeConfig = () => {
    setConfigCycle(null);
    setConfigErr('');
  };

  const challengeByCycle = useMemo(() => {
    const map = new Map();
    challenges.forEach((c) => {
      if (c.cycle_id != null) map.set(Number(c.cycle_id), c);
    });
    return map;
  }, [challenges]);

  const programsForCycle = useCallback((cycleId) => {
    const cycle = cycles.find((c) => Number(c.id) === Number(cycleId));
    if (cycle?.program_ids?.length) {
      return programs.filter((p) => cycle.program_ids.includes(String(p.id)) || cycle.program_ids.includes(p.id));
    }
    return programs.filter((p) => Number(p.active_cycle_id) === Number(cycleId));
  }, [programs, cycles]);

  const openConfig = (cycle) => {
    const existing = challengeByCycle.get(Number(cycle.id));
    const linkedPrograms = programsForCycle(cycle.id);
    setConfigCycle(cycle);
    setConfigForm({
      premio: existing?.premio || '1er lugar: $700.000 · 2do: $200.000 · 3ro: $100.000 COP',
      objetivo: existing?.objetivo || '',
      descripcion: existing?.descripcion || (existing ? '' : defaultPremioName(cycle)),
      program_id: existing?.program_id || linkedPrograms[0]?.id || programs[0]?.id || (gymScoped ? '' : 'vital'),
      min_classes: existing?.reglas?.min_classes ?? 8,
      instagram_live_url: existing?.reglas?.instagram_live_url || '',
    });
    setConfigErr('');
  };

  const saveConfig = async (e) => {
    e.preventDefault();
    if (!configCycle) return;
    setBusy(true);
    setConfigErr('');
    try {
      const { start, end } = cycleWindow(configCycle);
      const existing = challengeByCycle.get(Number(configCycle.id));
      const payload = {
        nombre: defaultPremioName(configCycle),
        descripcion: configForm.descripcion,
        objetivo: configForm.objetivo,
        premio: configForm.premio,
        program_id: configForm.program_id,
        cycle_id: configCycle.id,
        fecha_inicio: `${start}T08:00:00`,
        fecha_fin: `${end}T23:59:00`,
        reglas: {
          min_classes: Number(configForm.min_classes) || 8,
          photos_required: 4,
          instagram_live_url: configForm.instagram_live_url || '',
          prize_tiers: [
            { lugar: 1, monto_cop: 700000 },
            { lugar: 2, monto_cop: 200000 },
            { lugar: 3, monto_cop: 100000 },
          ],
        },
      };
      if (existing) {
        await api.put(`/d28d/challenges/${existing.id}`, payload);
      } else {
        await api.post('/d28d/challenges', payload);
      }
      setMsg('Premiación del ciclo guardada');
      closeConfig();
      await load();
    } catch (ex) {
      setConfigErr(ex?.response?.data?.error || 'Error al guardar');
    } finally {
      setBusy(false);
    }
  };

  const challengeAction = async (id, path) => {
    await api.post(`/d28d/challenges/${id}/${path}`);
    await load();
    if (selected?.id === id) {
      const res = await api.get(`/d28d/challenges/${id}`);
      setSelected(res.data?.data);
    }
  };

  const openEvaluate = async (challengeId, { sync = false } = {}) => {
    setErr('');
    setBusy(true);
    try {
      const res = await api.get(`/d28d/challenges/${challengeId}`, {
        params: sync ? { sync: 'true' } : {},
      });
      const data = res.data?.data;
      setSelected(data);
      const initialScores = {};
      (data?.participants || []).forEach((p) => {
        initialScores[p.id] = p.puntuacion ?? '';
      });
      setScores(initialScores);
      hydratePodiumFromData(data, setPodium);
    } catch (ex) {
      setErr(ex?.response?.data?.error || 'No se pudo cargar la evaluación');
    } finally {
      setBusy(false);
    }
  };

  const submitScore = async (entryId) => {
    if (!selected) return;
    const puntuacion = Number(scores[entryId]);
    if (!Number.isFinite(puntuacion)) {
      setErr('Ingresa una puntuación numérica');
      return;
    }
    setErr('');
    setBusy(true);
    try {
      await api.post(`/d28d/challenges/${selected.id}/score`, { entry_id: entryId, puntuacion });
      await openEvaluate(selected.id, { sync: false });
      setMsg('Puntuación guardada');
    } catch (ex) {
      setErr(ex?.response?.data?.error || 'No se pudo guardar la puntuación');
    } finally {
      setBusy(false);
    }
  };

  const submitPodium = async () => {
    if (!selected) return;
    const picks = [podium.first, podium.second, podium.third].filter(Boolean);
    if (picks.length === 0) {
      setErr('Selecciona al menos un participante para el podio');
      return;
    }
    setErr('');
    setBusy(true);
    try {
      await api.post(`/d28d/challenges/${selected.id}/podium`, podium);
      await challengeAction(selected.id, 'publish');
      await openEvaluate(selected.id, { sync: false });
      await load();
      setMsg('Podio publicado');
    } catch (ex) {
      setErr(ex?.response?.data?.error || 'No se pudo publicar el podio');
    } finally {
      setBusy(false);
    }
  };

  const participants = selected?.participants || [];

  return (
    <div className="d28d-admin-surface surface-readable space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2 d28d-page-title">
            <Trophy className="w-6 h-6" /> {gymScoped ? 'Premiaciones de tu sede' : 'Premiaciones por ciclo'}
          </h2>
          <p className="d28d-text-muted text-sm mt-1" style={{ margin: 0 }}>
            {gymScoped
              ? 'Premiación por ciclo de tu gimnasio virtual. Solo usuarios de tu sede participan en estos ciclos.'
              : 'Cada premiación pertenece a un ciclo de 28 días. Los usuarios se inscriben, suben medidas y fotos; aquí calificas y publicas el podio.'}
          </p>
        </div>
        {onBack && <button type="button" className="btn btn-secondary" onClick={onBack}>Volver</button>}
      </div>

      {msg && <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">{msg}</p>}
      {err && <p className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</p>}

      <div className="space-y-3">
        <h3 className="font-semibold text-base">Ciclos y premiaciones</h3>
        {cycles.length === 0 ? (
          <p className="text-sm text-stone-600">
            {gymScoped
              ? 'No hay ciclos en tu sede. Créalos en Maestros → Ciclos primero.'
              : 'No hay ciclos. Créalos en Programas D28D primero.'}
          </p>
        ) : (
          cycles.map((cycle) => {
            const challenge = challengeByCycle.get(Number(cycle.id));
            const linked = programsForCycle(cycle.id);
            const { start, end } = cycleWindow(cycle);
            return (
              <div
                key={cycle.id}
                className={`premio-cycle-row ${challenge?.estado === 'active' ? 'premio-cycle-row--active' : ''}`}
              >
                <div className="flex flex-wrap justify-between gap-3">
                  <div>
                    <p className="font-bold text-base">{cycle.name}</p>
                    <p className="text-sm text-stone-600">
                      {formatCycleDate(start)} → {formatCycleDate(end)}
                      {cycle.label ? ` · ${cycle.label}` : ''}
                    </p>
                    <p className="text-xs text-stone-500 mt-1">
                      Programas: {linked.length ? linked.map((p) => p.name).join(', ') : '— sin programa activo en este ciclo —'}
                    </p>
                    {challenge ? (
                      <>
                      <p className="text-sm mt-2">
                        <span className="font-semibold">{challenge.nombre}</span>
                        {' · '}
                        <span className="text-stone-600">{STATUS_LABEL[challenge.estado] || challenge.estado}</span>
                        {' · '}
                        <span className="text-stone-600">
                          {challenge.submissions_count ?? challenge.participants_count ?? 0} participantes
                        </span>
                        {challenge.premio ? ` · Premio: ${challenge.premio}` : ''}
                      </p>
                      {challenge.estado === 'published' && (challenge.podium?.length > 0 || challenge.ranking?.length > 0) ? (
                        <div className="mt-2 text-sm bg-lime-50 border border-lime-200 rounded-lg px-3 py-2">
                          <p className="font-semibold text-lime-900">Podio publicado</p>
                          {(challenge.podium || []).map((p) => (
                            <p key={p.lugar} className="text-stone-800">
                              {p.lugar}° — {p.user_name || `Participante #${p.entry_id}`}
                              {p.puntuacion != null ? ` (${p.puntuacion} pts)` : ''}
                            </p>
                          ))}
                        </div>
                      ) : null}
                      </>
                    ) : (
                      <p className="text-sm text-amber-800 mt-2">Sin premiación configurada para este ciclo.</p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 items-start">
                    <button type="button" className="btn btn-secondary text-xs inline-flex items-center gap-1 premio-action-btn" onClick={() => openConfig(cycle)}>
                      <Settings2 className="w-3 h-3" />
                      {challenge ? 'Editar premiación' : 'Configurar premiación'}
                    </button>
                    {challenge && (
                      <>
                        {challenge.estado === 'draft' && (
                          <button type="button" className="btn btn-primary text-xs inline-flex items-center gap-1 premio-action-btn" onClick={() => challengeAction(challenge.id, 'activate')}>
                            <Play className="w-3 h-3" /> Abrir inscripciones
                          </button>
                        )}
                        {challenge.estado === 'active' && (
                          <button type="button" className="btn btn-secondary text-xs inline-flex items-center gap-1 premio-action-btn" onClick={() => challengeAction(challenge.id, 'close')}>
                            <Lock className="w-3 h-3" /> Cerrar envíos
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-primary text-xs inline-flex items-center gap-1 premio-action-btn"
                          disabled={busy}
                          onClick={() => openEvaluate(challenge.id)}
                        >
                          Evaluar y podio
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {configCycle && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="premio-config-title"
          onClick={closeConfig}
        >
          <div
            className="d28d-admin-modal surface-readable border border-slate-200 rounded-xl p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <form onSubmit={saveConfig} className="space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 id="premio-config-title" className="font-semibold flex items-center gap-2 text-lg">
                    <Pencil className="w-4 h-4" />
                    Premiación del ciclo: {configCycle.name}
                  </h3>
                  <p className="text-sm text-stone-600 mt-1" style={{ margin: 0 }}>
                    Las fechas se toman del ciclo ({formatCycleDate(cycleWindow(configCycle).start)} — {formatCycleDate(cycleWindow(configCycle).end)}).
                  </p>
                </div>
                <button type="button" className="btn btn-secondary p-2 shrink-0 premio-action-btn" aria-label="Cerrar" onClick={closeConfig}>
                  <X className="w-4 h-4" />
                </button>
              </div>

              {configErr && (
                <p className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{configErr}</p>
              )}

              <div className="grid md:grid-cols-2 gap-3">
                <label className="ui-field md:col-span-2">
                  <span className="ui-field__label">Programa</span>
                  <select className="ui-input mt-1" value={configForm.program_id} onChange={(e) => setConfigForm({ ...configForm, program_id: e.target.value })}>
                    {programs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </label>
                <label className="ui-field md:col-span-2">
                  <span className="ui-field__label">Descripción (visible para usuarios)</span>
                  <input className="ui-input mt-1" required value={configForm.descripcion} onChange={(e) => setConfigForm({ ...configForm, descripcion: e.target.value })} />
                </label>
                <label className="ui-field">
                  <span className="ui-field__label">Premio</span>
                  <input className="ui-input mt-1" placeholder="Ej. Kit wellness, membresía…" value={configForm.premio} onChange={(e) => setConfigForm({ ...configForm, premio: e.target.value })} />
                </label>
                <label className="ui-field">
                  <span className="ui-field__label">Objetivo / prueba</span>
                  <input className="ui-input mt-1" placeholder="Ej. Mayor reducción de medidas" value={configForm.objetivo} onChange={(e) => setConfigForm({ ...configForm, objetivo: e.target.value })} />
                </label>
                <label className="ui-field">
                  <span className="ui-field__label">Mínimo de clases</span>
                  <input type="number" min={1} className="ui-input mt-1" value={configForm.min_classes} onChange={(e) => setConfigForm({ ...configForm, min_classes: e.target.value })} />
                </label>
                <label className="ui-field md:col-span-2">
                  <span className="ui-field__label">Instagram Live (URL premiación)</span>
                  <input className="ui-input mt-1" placeholder="https://instagram.com/…" value={configForm.instagram_live_url} onChange={(e) => setConfigForm({ ...configForm, instagram_live_url: e.target.value })} />
                </label>
              </div>
              <div className="flex gap-2 flex-wrap pt-1">
                <button type="submit" className="btn btn-primary premio-action-btn" disabled={busy}>Guardar premiación del ciclo</button>
                <button type="button" className="btn btn-secondary premio-action-btn" onClick={closeConfig}>Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="premio-eval-title"
          onClick={() => setSelected(null)}
        >
          <div
            className="d28d-admin-modal d28d-admin-modal--evaluate surface-readable border border-slate-200 rounded-xl p-5 shadow-2xl max-h-[94vh] overflow-y-auto w-full space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
          <div className="flex justify-between items-start gap-3 flex-wrap sticky top-0 bg-white/95 backdrop-blur pb-2 border-b border-slate-100 z-10">
            <div>
              <h3 id="premio-eval-title" className="font-bold text-lg">Evaluación: {selected.nombre}</h3>
              <p className="text-sm text-stone-600">{selected.objetivo || 'Revisa medidas, fotos y asigna puntuación.'}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="btn btn-secondary text-sm premio-action-btn"
                disabled={busy}
                onClick={() => openEvaluate(selected.id, { sync: true })}
                title="Resincroniza participantes del ciclo (más lento)"
              >
                Sincronizar
              </button>
              <button type="button" className="btn btn-secondary text-sm premio-action-btn" onClick={() => setSelected(null)}>Cerrar</button>
            </div>
          </div>

          {err && (
            <p className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</p>
          )}
          {msg && (
            <p className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">{msg}</p>
          )}

          {participants.length === 0 ? (
            <p className="text-sm text-stone-600">
              Aún no hay participantes con fotos, medidas o pruebas físicas en este ciclo.
              Los usuarios deben subir material desde Inicio o Mi Progreso.
            </p>
          ) : (
            <div className="space-y-4">
              {participants.filter((p) => p.estado !== 'withdrawn').map((p) => (
                <div key={p.id} className="participant-card">
                  <div className="flex flex-wrap justify-between gap-2 mb-2">
                    <div>
                      <p className="font-semibold">{p.user_name}</p>
                      <p className="text-xs text-stone-500">
                        {p.user_email || `ID #${p.user_id}`}
                        {' · '}
                        Fotos: {p.photos_slots_filled ?? 0}/{p.photos_required ?? 8}
                        {p.fitness_total != null ? ` · Prueba física: ${p.fitness_total} pts` : ''}
                        {p.classes_attended != null ? ` · Clases: ${p.classes_attended}` : ''}
                      </p>
                      <p className="text-xs text-stone-500">
                        Estado: {p.estado}
                        {p.has_measures ? ' · Medidas ✓' : ''}
                        {p.eligibility?.eligible ? ' · Postulación completa' : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        className="ui-input w-24 text-sm"
                        type="number"
                        min="0"
                        placeholder="Pts"
                        value={scores[p.id] ?? ''}
                        onChange={(e) => setScores({ ...scores, [p.id]: e.target.value })}
                      />
                      <button type="button" className="btn btn-primary text-xs premio-action-btn" onClick={() => submitScore(p.id)}>Calificar</button>
                    </div>
                  </div>
                  {(p.evidences || []).length === 0 && !(p.progress_photos || []).length && !p.fitness_has_result && !p.fitness_scores ? (
                    <p className="text-xs text-stone-500">Sin evidencias enviadas.</p>
                  ) : (
                    <>
                      {(p.progress_photos || []).length > 0 ? (
                        <ParticipantPhotoGrid photos={p.progress_photos} />
                      ) : null}
                      <ParticipantFitnessGrid
                        scores={p.fitness_scores}
                        total={p.fitness_total}
                        testName={p.fitness_test_name}
                        evidences={p.evidences}
                      />
                      {(p.evidences || []).filter((ev) => ev.tipo !== 'image' && ev.tipo !== 'fitness_test').length > 0 ? (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
                          {(p.evidences || []).filter((ev) => ev.tipo !== 'image' && ev.tipo !== 'fitness_test').map((ev) => (
                            <div key={ev.id} className="border border-slate-200 rounded-xl p-2 bg-slate-50 text-sm">
                              <p className="text-xs font-semibold uppercase text-stone-500 mb-1">
                                {ev.tipo === 'measures' ? 'Medidas' : ev.tipo}
                              </p>
                              {ev.tipo === 'measures' || ev.tipo === 'text' ? (
                                <pre className="text-xs text-stone-800 whitespace-pre-wrap overflow-x-auto">{ev.contenido}</pre>
                              ) : null}
                              {(ev.tipo === 'image' || ev.url) && ev.url ? (
                                <EvidenceThumb src={ev.url} alt="Evidencia" />
                              ) : null}
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="border-t border-slate-200 pt-4 space-y-3">
            <h4 className="font-semibold">Asignar podio</h4>
            <p className="text-xs text-stone-600">Elige los tres mejores resultados y publica el podio para todos los usuarios.</p>
            <div className="grid md:grid-cols-3 gap-3">
              {['first', 'second', 'third'].map((place, idx) => (
                <label key={place} className="ui-field">
                  <span className="ui-field__label">{idx + 1}° lugar</span>
                  <select
                    className="ui-input mt-1"
                    value={podium[place]}
                    onChange={(e) => setPodium({ ...podium, [place]: e.target.value })}
                  >
                    <option value="">— Seleccionar participante —</option>
                    {participants.filter((p) => p.estado !== 'withdrawn').map((p) => (
                      <option key={p.id} value={p.id}>{p.user_name} {p.puntuacion != null ? `(${p.puntuacion} pts)` : ''}</option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
            <button type="button" className="btn btn-primary inline-flex gap-2 premio-action-btn" disabled={busy} onClick={submitPodium}>
              <Megaphone className="w-4 h-4" /> Publicar podio
            </button>
          </div>
          </div>
        </div>
      )}
    </div>
  );
}
